/**
 * Maps get-apollo / leadsTotal responses to i18n keys under lang.label.
 *
 * @param {object|string|null} response
 * @returns {{ key: string, params?: object }|null}
 */
export function parseContactSearchError(response) {
  if (!response) {
    return { key: 'contact_search_error_generic' };
  }

  if (response === 'Invalid access credentials.') {
    return { key: 'contact_search_error_invalid_credentials' };
  }

  const appliedFilters = Array.isArray(response.applied_filters)
    ? response.applied_filters
    : [];
  const pagination = response.pagination || {};
  const totalEntries = normalizeContactTotal(pagination.total_entries);
  const salesridge = response.salesridge;
  const salesridgeData = salesridge && salesridge.data ? salesridge.data : null;
  const salesridgeTotal = salesridgeData
    ? normalizeContactTotal(salesridgeData.total)
    : null;

  // A completed search that returns zero contacts is not an error.
  if (salesridgeData && !salesridge.salesridge_error && totalEntries === 0) {
    return null;
  }

  if (!salesridge || !salesridge.salesridge_error) {
    return null;
  }

  // Zero results with industries successfully sent — not an unrecognized-industry issue.
  if (shouldTreatAsZeroContactResult(totalEntries, salesridgeTotal, appliedFilters)) {
    return null;
  }

  const raw = salesridge.salesridge_raw || salesridge;
  const debug = raw.debug || {};
  const responseData = debug.responseData || {};
  const errorCode = raw.error_code || responseData.code || '';
  const errorMessage = raw.error || responseData.message || '';

  if (errorCode === 'error.filters.enum.invalid') {
    if (shouldTreatAsZeroContactResult(totalEntries, salesridgeTotal, appliedFilters)) {
      return null;
    }

    const values = extractEnumValues(errorMessage);
    if (values.length > 0) {
      return {
        key: 'contact_search_error_invalid_industry',
        params: { values: values.join(', ') },
      };
    }
    return { key: 'contact_search_error_invalid_industry_generic' };
  }

  if (shouldTreatAsZeroContactResult(totalEntries, salesridgeTotal, appliedFilters)) {
    return null;
  }

  return { key: 'contact_search_error_generic' };
}

function normalizeContactTotal(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const total = Number(value);
  return Number.isFinite(total) ? total : null;
}

const INDUSTRY_CATALOG_IGNORE_REASONS = new Set([
  'industry_not_in_catalog',
  'no_fullenrich_label',
  'industry_comma_in_label',
]);

function hasIgnoredIndustryFilters(appliedFilters) {
  return appliedFilters.some(
    (filter) =>
      (filter.type === 'category_industry' ||
        filter.type === 'category_industry_exclude') &&
      filter.status === 'ignored' &&
      INDUSTRY_CATALOG_IGNORE_REASONS.has(filter.reason)
  );
}

function shouldTreatAsZeroContactResult(totalEntries, salesridgeTotal, appliedFilters) {
  const isZero =
    totalEntries === 0 || (salesridgeTotal !== null && salesridgeTotal === 0);
  if (!isZero) {
    return false;
  }

  if (hasIgnoredIndustryFilters(appliedFilters)) {
    return false;
  }

  const industryFilters = appliedFilters.filter(
    (filter) => filter.type === 'category_industry'
  );
  if (!industryFilters.length) {
    return true;
  }

  return industryFilters.every((filter) => filter.status === 'applied');
}

function extractEnumValues(message) {
  if (!message || typeof message !== 'string') {
    return [];
  }

  const values = [];
  const re = /='([^']+)'/g;
  let match;
  while ((match = re.exec(message)) !== null) {
    const value = (match[1] || '').trim();
    if (value && value !== '[object Object]' && !values.includes(value)) {
      values.push(value);
    }
  }

  return values;
}

/**
 * @param {import('vue-i18n').default} i18n
 * @param {{ key: string, params?: object }|null} error
 * @returns {string|null}
 */
export function formatContactSearchError(i18n, error) {
  if (!error || !error.key) {
    return null;
  }

  const path = `lang.label.${error.key}`;
  if (error.params) {
    return i18n.t(path, error.params);
  }
  return i18n.t(path);
}
